import { z } from "zod";

const color = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/** Visual tokens for the plug-in modules (form, proof, authoring). */
export const moduleThemeSchema = z
  .object({
    mode: z.enum(["system", "light", "dark"]).optional(),
    primary: color.optional(),
    accent: color.optional(),
    ink: color.optional(),
    muted: color.optional(),
    background: color.optional(),
    surface: color.optional(),
    border: color.optional(),
    radius: z.number().min(0).max(24).optional(),
    font: z
      .enum(["Inter", "System", "Georgia", "Libre Baskerville", "Monospace"])
      .optional(),
    fontSize: z.number().min(11).max(18).optional(),
    density: z.enum(["compact", "comfortable", "spacious"]).optional(),
  })
  .strict();
export type ModuleTheme = z.infer<typeof moduleThemeSchema>;
export type ModuleThemeKey = keyof ModuleTheme;
/** Site settings apply to every company; a company overrides any token. */
export const appearanceSettingsSchema = z
  .object({
    site: moduleThemeSchema.default({}),
    company: moduleThemeSchema.default({}),
  })
  .strict();
export type AppearanceSettings = z.infer<typeof appearanceSettingsSchema>;
export const defaultModuleTheme: Required<ModuleTheme> = {
  mode: "system",
  primary: "#47735d",
  accent: "#486fbe",
  ink: "#263b36",
  muted: "#7c8585",
  background: "#f7f8fa",
  surface: "#ffffff",
  border: "#e6e9e9",
  radius: 8,
  font: "Inter",
  fontSize: 13,
  density: "comfortable",
};
export function resolveModuleTheme(
  appearance?: Partial<AppearanceSettings>,
): Required<ModuleTheme> {
  const clean = (theme?: ModuleTheme) =>
    Object.fromEntries(
      Object.entries(theme || {}).filter(([, value]) => value !== undefined),
    );
  return {
    ...defaultModuleTheme,
    ...clean(appearance?.site),
    ...clean(appearance?.company),
  } as Required<ModuleTheme>;
}
const fonts: Record<Required<ModuleTheme>["font"], string> = {
  Inter: "Inter, system-ui, sans-serif",
  System: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  Georgia: "Georgia, 'Times New Roman', serif",
  "Libre Baskerville": "'Libre Baskerville', Georgia, serif",
  Monospace: "ui-monospace, 'SFMono-Regular', Menlo, monospace",
};
/** Readable text colour for a filled background. */
function onColor(hex: string) {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#1b2421" : "#ffffff";
}
/**
 * CSS that applies a module theme inside `scope`. Colour surfaces apply in
 * light mode; brand colour, shape and type apply in both modes.
 */
export function moduleThemeCss(
  theme: Required<ModuleTheme>,
  scope = ".proof-authoring",
): string {
  const t = moduleThemeSchema.parse(theme) as Required<ModuleTheme>;
  const pad =
    t.density === "compact" ? 0.8 : t.density === "spacious" ? 1.25 : 1;
  const light = `:root:not([data-theme="dark"]) ${scope}`;
  const css = [
    `${scope}{--primary:${t.primary};--blue:${t.accent};--module-radius:${t.radius}px;--module-density:${pad};font-family:${fonts[t.font]};font-size:${t.fontSize}px}`,
    `${light}{--ink:${t.ink};--muted:${t.muted};--line:${t.border};color:${t.ink}}`,
    `${light} .app-main,${light}.app-main,${light} .app-content{background:${t.background}}`,
    `${light} .panel,${light} .modal,${light} .proof-review-panel,${light} .proof-input-panel,${light} .profile-grid{background:${t.surface};border-color:${t.border}}`,
    `${light} input:not([type=checkbox]):not([type=radio]):not([type=color]),${light} select,${light} textarea{border-color:${t.border}}`,
    `${scope} .panel,${scope} .modal,${scope} .profile-grid,${scope} .resource-hub-card{border-radius:${Math.round(t.radius * 1.5)}px}`,
    `${scope} .button,${scope} input:not([type=checkbox]):not([type=radio]),${scope} select,${scope} textarea{border-radius:${t.radius}px}`,
    `${scope} .button.primary,${scope} .button.dark,${scope} .proof-customize{background:${t.primary};border-color:${t.primary};color:${onColor(t.primary)}}`,
    `${scope} .app-content a,${scope} .modal a,${scope} .app-content .text-button,${scope}.appearance-preview-module a{color:${t.accent}}`,
  ];
  if (t.density !== "comfortable")
    css.push(
      // Collapsible panels keep their compact single-row header.
      `${scope} .form-group,${scope} .panel.padded:not(details){padding:calc(20px * var(--module-density))}`,
    );
  return css.join("\n");
}
